import type { ReactElement } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@wlb/shared/context/AuthContext';
import { SurveyProvider } from '@wlb/shared/context/SurveyContext';
import { FeedProvider } from '@wlb/shared/context/FeedContext';
import { ThemeProvider } from '@wlb/shared/theme';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { BrandLogo } from './components/ui/BrandLogo';
import { Toast } from './components/Toast';
import { AuthGateProvider } from './components/feedback/AuthGate';
import { ErrorBoundary } from './ErrorBoundary';
import { SessionExpiredModal } from './components/feedback/SessionExpiredModal';
import { AppShell } from './components/layout/AppShell';
import { ThemeApplier } from './components/layout/ThemeApplier';
import { useCrossTabSession } from './features/auth/useCrossTabSession';
import type { AuthFlowState } from './features/auth/authFlowState';
import { Home } from './pages/Home';
import { ComingSoon } from './pages/ComingSoon';
import { CreatePost } from './pages/CreatePost';
import { CreateGroup } from './pages/CreateGroup';
import { EditGroup } from './pages/EditGroup';
import { GroupDetails } from './pages/GroupDetails';
import { MyGroupsList } from './pages/MyGroupsList';
import { SignUp } from './pages/SignUp';
import { SignUpProfile } from './pages/SignUpProfile';
import { BasicInfo } from './pages/BasicInfo';
import { UploadImage } from './pages/UploadImage';
import { SurveyQ1 } from './pages/SurveyQ1';
import { SurveyQuestion } from './pages/SurveyQuestion';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { VerificationCode } from './pages/VerificationCode';
import { ResetPassword } from './pages/ResetPassword';
import { PasswordResetSuccess } from './pages/PasswordResetSuccess';
import { WelcomeSurvey } from './pages/WelcomeSurvey';
import { Buddies } from './pages/Buddies';
import { MatchLoading } from './pages/MatchLoading';
import { MatchResults } from './pages/MatchResults';
import { AiBuddyOnboarding } from './pages/AiBuddyOnboarding';
import { MyProfile } from './pages/MyProfile';
import { OtherProfile } from './pages/OtherProfile';
import { Connections } from './pages/Connections';
import { UpdateProfile } from './pages/UpdateProfile';
import { ViewPortrait } from './pages/ViewPortrait';
import { BlockedUsers } from './pages/BlockedUsers';
import { Blog } from './pages/Blog';
import { SavedPosts } from './pages/SavedPosts';
import { ChooseAiBuddy } from './pages/ChooseAiBuddy';
import { Tracker } from './pages/Tracker';
import { Awards } from './pages/Awards';
import { Leaderboard } from './pages/Leaderboard';
import { ChangePassword } from './pages/ChangePassword';
import { AiSettings } from './pages/AiSettings';
import { AccountSettings } from './pages/AccountSettings';
import { ContactUs } from './pages/ContactUs';
import { TermsOfUse } from './pages/TermsOfUse';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { Notifications } from './pages/Notifications';
import { Messages } from './pages/Messages';
import { Chat } from './pages/Chat';
import { ChatDetails } from './pages/ChatDetails';
import { CreateGroupChat } from './pages/CreateGroupChat';
import { Robi } from './pages/Robi';
import { DeepLinkLanding } from './pages/DeepLinkLanding';

/** Sends anonymous visitors to login, remembering where they were headed. */
function ProtectedRoute({ children }: { children: ReactElement }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (isAuthenticated) {
    return children;
  }
  const state: AuthFlowState = { from: `${location.pathname}${location.search}` };
  return <Navigate to="/login" replace state={state} />;
}

/** Keeps signed-in users out of the login / password-reset screens. */
function AnonymousRoute({ children }: { children: ReactElement }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return children;
  }
  // This guard unmounts the login screen in the same commit that flips
  // isAuthenticated, so the screen's own redirect effect never runs. The
  // deep link ProtectedRoute stashed has to be honoured here.
  const from = (location.state as AuthFlowState | null)?.from;
  return <Navigate to={from ?? '/'} replace />;
}

/**
 * Gates the post-account-creation onboarding steps.
 *
 * Account creation stores a live token but deliberately leaves
 * `isAuthenticated` false until `completeAuthSession()` runs at the end of
 * onboarding, so these routes cannot use ProtectedRoute — it would bounce the
 * user to /login mid-flow. A token is the proof that the account exists.
 */
function OnboardingRoute({ children }: { children: ReactElement }) {
  const { isAuthenticated, token } = useAuth();
  return isAuthenticated || token ? children : <Navigate to="/signup" replace />;
}

function AppErrorFallback({ retry }: { retry: () => void }) {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <main className="app-error" role="alert" aria-labelledby="app-error-title">
      <div className="app-error__card">
        <BrandLogo className="app-error__logo" />
        <div className="app-error__copy">
          <p className="app-error__eyebrow">{t('common.errors.friendlyEyebrow')}</p>
          <h1 id="app-error-title">{t('common.errors.friendlyTitle')}</h1>
          <p>{t('common.errors.friendlyMessage')}</p>
          <p className="app-error__support">{t('common.errors.supportMessage')}</p>
        </div>
        <div className="app-error__actions">
          <button type="button" onClick={retry}>{t('common.buttons.retry')}</button>
          <button type="button" className="app-error__secondary-action" onClick={() => navigate('/', { replace: true })}>
            {t(isAuthenticated ? 'home.topTabs.wlb' : 'home.topTabs.trending')}
          </button>
          {!isAuthenticated && (
            <button type="button" className="app-error__secondary-action" onClick={() => navigate('/login')}>
              {t('common.buttons.logIn')}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

/** Stable ErrorBoundary render prop — defined at module scope to avoid remounts. */
function renderAppErrorFallback(_error: Error, retry: () => void) {
  return <AppErrorFallback retry={retry} />;
}

function AppRoutes() {
  const { initializing } = useAuth();
  const { t } = useTranslation();
  useCrossTabSession();

  if (initializing) {
    return <div className="app-loader" role="status">{t('common.loading')}</div>;
  }

  return (
    <Routes>
      {/* Standalone auth pages — unavailable once signed in */}
      <Route path="/login" element={<AnonymousRoute><Login /></AnonymousRoute>} />
      {/* Merged into the single /login screen; kept as a redirect for deep links. */}
      <Route path="/login-password" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<AnonymousRoute><ForgotPassword /></AnonymousRoute>} />
      <Route path="/verification-code" element={<AnonymousRoute><VerificationCode /></AnonymousRoute>} />
      <Route path="/reset-password" element={<AnonymousRoute><ResetPassword /></AnonymousRoute>} />
      <Route
        path="/password-reset-success"
        element={<AnonymousRoute><PasswordResetSuccess /></AnonymousRoute>}
      />
      <Route path="/signup" element={<AnonymousRoute><SignUp /></AnonymousRoute>} />
      <Route path="/signup/profile" element={<AnonymousRoute><SignUpProfile /></AnonymousRoute>} />
      {/* Deep link / App link landing & web fallback pages */}
      <Route path="/profile/:userId" element={<DeepLinkLanding forcedType="profile" />} />
      <Route path="/u/:userId" element={<DeepLinkLanding forcedType="profile" />} />
      <Route path="/group/:groupId" element={<DeepLinkLanding forcedType="group" />} />
      <Route path="/post/:postId" element={<DeepLinkLanding forcedType="post" />} />
      {/* Onboarding — reachable while the session is still being set up */}
      {([
        ['/basic-info', <BasicInfo />],
        ['/upload-image', <UploadImage />],
        ['/survey', <WelcomeSurvey />],
        ['/survey/q1', <SurveyQ1 />],
        ['/survey/:questionId', <SurveyQuestion />],
        ['/onboarding/matches/loading', <MatchLoading />],
        ['/onboarding/matches', <MatchResults />],
        ['/onboarding/ai-buddy', <AiBuddyOnboarding />],
      ] as const).map(([path, element]) => (
        <Route key={path} path={path} element={<OnboardingRoute>{element}</OnboardingRoute>} />
      ))}

      {/* App shell — home is browsable anonymously; member areas require a session */}
      <Route element={<AppShell />}>
        <Route path="/" element={<Home />} />
        {/* Legal pages stay readable without a session, like the home feed. */}
        <Route path="/support/terms" element={<TermsOfUse />} />
        <Route path="/support/privacy" element={<PrivacyPolicy />} />
        {([
          ['/buddies', <Buddies />],
          ['/create', <CreatePost />],
          ['/groups/create', <CreateGroup />],
          ['/groups/my', <MyGroupsList />],
          ['/groups/:groupId/edit', <EditGroup />],
          ['/groups/:groupId', <GroupDetails />],
          ['/notifications', <Notifications />],
          ['/robi', <Robi />],
          ['/messages', <Messages />],
          ['/messages/group/new', <CreateGroupChat />],
          ['/messages/:conversationId/details', <ChatDetails />],
          ['/messages/:conversationId', <Chat />],
          ['/profile/others/:userGuid', <OtherProfile />],
          ['/profile/others/:userGuid/connections', <Connections />],
          ['/profile/connections', <Connections />],
          ['/profile/edit', <UpdateProfile />],
          ['/profile/portrait', <ViewPortrait />],
          ['/profile', <MyProfile />],
          ['/blocked-users', <BlockedUsers />],
          ['/blog', <Blog />],
          ['/saved-posts', <SavedPosts />],
          ['/choose-ai-buddy', <ChooseAiBuddy />],
          ['/tracker', <Tracker />],
          ['/awards', <Awards />],
          ['/leaderboard', <Leaderboard />],
          ['/settings', <Navigate to="/settings/account" replace />],
          ['/change-password', <ChangePassword />],
          ['/settings/ai', <AiSettings />],
          ['/settings/account', <AccountSettings />],
          ['/support/guide', <ComingSoon titleKey="sidebar.support.help" />],
          ['/support/contact', <ContactUs />],
        ] as const).map(([path, element]) => (
          <Route key={path} path={path} element={<ProtectedRoute>{element}</ProtectedRoute>} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ThemeApplier />
      <AuthProvider>
        <SurveyProvider>
          {/* FeedProvider only fetches once authenticated — safe for guests. */}
          <FeedProvider>
            <BrowserRouter>
              <AuthGateProvider>
                <ErrorBoundary fallback={renderAppErrorFallback}>
                  <AppRoutes />
                  <SessionExpiredModal />
                  <Toast />
                </ErrorBoundary>
              </AuthGateProvider>
            </BrowserRouter>
          </FeedProvider>
        </SurveyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
